import { DeleteOutlined, DownOutlined, PlusCircleFilled } from '@ant-design/icons'
import { PageHeader } from '@ant-design/pro-layout'
import { treeUtil } from '@salescore/buff-common'
import type { ViewFieldsFragment } from '@salescore/client-api'
import { CONSTANT, Posthog, POSTHOG_EVENTS } from '@salescore/client-base'
import {
  type ConditionalEffectConfig,
  coreDslFormNodeSchema,
  type CoreDslFormNodeStatePartial,
  generateSalesforceHistoryModelName,
  type ViewConfigField,
} from '@salescore/core'
import { generateRandomString } from '@salescore/frontend-common'
import { App, Avatar, Button, Col, Divider, Dropdown, Form, type ModalFuncProps, Row, Select, Space } from 'antd'
import { Array } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { type ReactElement, useEffect, useMemo, useState } from 'react'
import { useRecoilValue } from 'recoil'

import { CoreDslForm } from '../../../components/view_ui/ViewUISheetWithNavigation/SheetNavigation/expression/CoreDslForm'
import { changesAtom } from '../../../recoil/recordsHistory'
import { useGetModel, useGetModelAndProperty } from '../../../recoil/view/selectors/connectionsSelector'
import { useSetFieldMutation, useViewConfigSheet } from '../../../recoil/viewConfig'

//
// ハイライト条件用のモーダル
// salesforceの項目履歴管理が有効になっていないと使えない
//
interface Properties {
  configField: ViewConfigField
  columnTitle: string
  onFinish: () => void
  view: ViewFieldsFragment
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}

export const buildHighlightFormModalProperties = ({
  configField,
  columnTitle,
  onFinish,
  view,
  setHasChangeToViewSchema,
}: Properties): ModalFuncProps => ({
  icon: null,
  // 絞り込みモーダル (ViewQueryFiltersModal) と同じ条件フォームを表示するため幅を揃える。
  // 狭いと同じ画面幅でもこちらだけ演算子ラベルが先に見切れる
  width: '90%',
  okButtonProps: { style: { display: 'none' } },
  cancelText: t(`閉じる`),
  maskClosable: true,
  title: <PageHeader title={<span>{t(`{{title}}のハイライト条件設定`, { title: columnTitle })}</span>} />,
  content: (
    <HighlightForm
      configField={configField}
      onFinish={onFinish}
      view={view}
      setHasChangeToViewSchema={setHasChangeToViewSchema}
    />
  ),
  style: {
    maxWidth: 1000,
    padding: 0,
  },
  destroyOnClose: true,
  footer: null,
  closable: true,
})

interface HighlightColor {
  color: string
  label?: string
}

interface ConditionalEffectFormItemState {
  highlightColor?: HighlightColor
  ast?: CoreDslFormNodeStatePartial
  key: string // jsxのためのuniqueなkey（適当に生成）
}

const highlightColorMapper: Record<string, string> = {
  negative: '#F65948' as const,
  positive: '#3953F9' as const,
}
const presetColors = [
  `#0081C2`,
  `#3953F9`,
  `#6836FF`,
  `#8201E7`,
  `#B0007F`,
  `#DB0024`,
  `#F07801`,
  `#F5C001`,
  `#0FBD01`,
  `#00A4B6`,
]
export const highlightRibonMapper = new Map<string, string>([
  ['#F65948', '#C32615'],
  ['#3953F9', '#0620C6'],
  ['#0081C2', '#004E8F'],
  ['#6836FF', '#3503CC'],
  ['#8201E7', '#4F00B4'],
  ['#B0007F', '#7D004C'],
  ['#DB0024', '#A80000'],
  ['#F07801', '#BD4500'],
  ['#F5C001', '#C28D00'],
  ['#0FBD01', '#008A00'],
  ['#00A4B6', '#007183'],
])
const negativeOption: HighlightColor = {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  color: highlightColorMapper.negative!,
  label: t(`ネガティブ`),
}

const HighlightForm = ({
  configField,
  onFinish,
  view,
  setHasChangeToViewSchema,
}: {
  configField: ViewConfigField
  onFinish: () => void
  view: ViewFieldsFragment
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const config = useViewConfigSheet()
  const fields = config.fields ?? []
  const sameNodeFields = useMemo(
    () => fields.filter((field) => field.property.nodeName === configField.property.nodeName),
    [configField.property.nodeName, fields],
  )
  const setField = useSetFieldMutation()
  const changes = useRecoilValue(changesAtom)
  const { message } = App.useApp()

  const [effects, setEffects] = useState<ConditionalEffectFormItemState[]>(() => {
    const highlights = (configField.conditionalEffects ?? []).flatMap((x) => (x.type === 'form' ? x : []))
    if (highlights.length === 0) {
      return [
        {
          highlightColor: negativeOption,
          ast: undefined,
          key: generateRandomString(),
        },
      ]
    }
    return highlights.map((x): ConditionalEffectFormItemState => ({
      highlightColor: x.highlightColor,
      ast: x.ast,
      key: generateRandomString(),
    }))
  })
  const getModelAndProperty = useGetModelAndProperty()
  const getModel = useGetModel()
  const { model, property, historyModel } = useMemo(() => {
    const propertyWithModel = getModelAndProperty(configField.property.modelName, configField.property.propertyName)
    const historyModel = getModel(generateSalesforceHistoryModelName(configField.property.modelName))
    if (propertyWithModel === undefined) {
      return { model: undefined, property: undefined, historyModel }
    }
    return {
      ...propertyWithModel,
      historyModel,
    }
  }, [configField, getModelAndProperty, getModel])
  useEffect(() => {
    if (changes.length > 0) {
      message.warning(t('設定を変更すると変更情報が失われます。'))
    }
  }, [changes.length, message])

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition,sonarjs/different-types-comparison
  if (model === undefined || property === undefined) {
    return <></> // ありえないはず
  }

  return (
    <Form
      layout="vertical"
      onFinish={() => {
        const conditionalEffects = effects
          .map((effect): ConditionalEffectConfig | undefined => {
            if (effect.ast === undefined) {
              return undefined
            }
            const parsed = coreDslFormNodeSchema.safeParse(effect.ast)
            // eslint-disable-next-line sonarjs/todo-tag
            // TODO: 中途半端に入力されてないときの対応
            if (!parsed.success) {
              return undefined
            }
            const ast = parsed.data
            const allLeafs = treeUtil.flatNodes(ast).flatMap((x) => x.leafs)
            if (allLeafs.length === 0) {
              // 条件が何も設定されていなければ設定しない
              return undefined
            }
            return {
              type: 'form',
              effect: `highlight` as const,
              highlightColor: effect.highlightColor ?? negativeOption,
              ast: parsed.data,
            }
          })
          .filter(isNotNullish)

        setField({
          field: {
            ...configField,
            conditionalEffects,
          },
          onSetConfig: () => {
            setHasChangeToViewSchema(true)
          },
        })
        message.success(t(`条件付き書式を設定しました`))
        onFinish()
        Posthog.track(POSTHOG_EVENTS.save_highlight_conditional_effect, { viewId: view.id })
      }}
    >
      {effects.map((effect, index) => (
        // eslint-disable-next-line @eslint-react/no-missing-key
        <>
          <Row justify="space-between" key={effect.key}>
            <Col span={12}>
              <Form.Item label="" rules={[{ required: true, message: t(`効果を入力してください`) }]}>
                <HighlightColorPicker
                  value={effect.highlightColor}
                  onChange={(highlightColor) => {
                    setEffects((xs) => [
                      ...xs.slice(0, index),
                      {
                        ...effect,
                        highlightColor,
                      },
                      ...xs.slice(index + 1),
                    ])
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8} className="text-right">
              <Form.Item label="">
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    setEffects((xs) => [...xs.slice(0, index), ...xs.slice(index + 1)])
                  }}
                >
                  {t(`削除`)}
                </Button>
              </Form.Item>
            </Col>
            <Col span={24}>
              <CoreDslForm
                isAdvancedMode
                targetProperty={{
                  property,
                  model,
                  nodePropertyName: configField.property,
                  historyModel,
                }}
                initialValue={effect.ast}
                availableFields={sameNodeFields}
                onChange={(ast) => {
                  setEffects((xs) => [
                    ...xs.slice(0, index),
                    {
                      ...effect,
                      ast,
                    },
                    ...xs.slice(index + 1),
                  ])
                }}
              />
            </Col>
          </Row>
          {effects.length - 1 !== index && <Divider />}
        </>
      ))}
      <Row justify="center">
        <Space>
          <Button
            type="text"
            icon={<PlusCircleFilled />}
            color={'blue'}
            style={{
              color: CONSTANT.colors.primaryColor,
            }}
            onClick={() => {
              setEffects((xs) => [
                ...xs,
                {
                  highlightColor: negativeOption,
                  ast: undefined,
                  key: generateRandomString(),
                },
              ])
            }}
          >
            {t(`効果を追加`)}
          </Button>
        </Space>
      </Row>
      <Divider />
      <Row justify="end" className="mt-4">
        <Space>
          <Button htmlType="submit">{t(`適用`)}</Button>
        </Space>
      </Row>
    </Form>
  )
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
function HighlightColorPicker({
  value,
  onChange,
}: {
  value: HighlightColor | undefined
  onChange: (value: HighlightColor) => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <Dropdown
      open={open}
      onOpenChange={(open) => {
        setOpen(open)
      }}
      menu={{
        items: [
          {
            key: ``,
            label: (
              <div
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
              >
                <Form layout="vertical">
                  <Form.Item label={t(`種類`)} style={{ width: 200 }}>
                    <Select
                      onChange={(value) => {
                        if (value === 'negative') {
                          onChange({
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            color: highlightColorMapper.negative!,
                            label: t(`ネガティブ`),
                          })
                          setOpen(false)
                          return
                        }
                        onChange({
                          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                          color: highlightColorMapper.positive!,
                          label: t(`ポジティブ`),
                        })
                        setOpen(false)
                      }}
                      options={[
                        {
                          value: `negative` as const,
                          label: <span style={{ color: highlightColorMapper.negative }}>{t(`ネガティブ`)}</span>,
                        },
                        {
                          value: `positive` as const,
                          label: <span style={{ color: highlightColorMapper.positive }}>{t(`ポジティブ`)}</span>,
                        },
                      ]}
                    />
                  </Form.Item>
                  <Form.Item label={t(`カラー`)}>
                    {Array.chunksOf(presetColors, 5).map((colors) => (
                      <div key={colors.join(',')} className="mb-4 flex w-full justify-between">
                        {colors.map((color) => (
                          <Avatar
                            key={color}
                            size={22}
                            style={{ backgroundColor: color }}
                            onClick={() => {
                              onChange({
                                color,
                              })
                              setOpen(false)
                            }}
                          />
                        ))}
                      </div>
                    ))}
                  </Form.Item>
                </Form>
              </div>
            ),
          },
        ],
      }}
    >
      <Space>
        <span>{t(`ハイライト色を選択`)}</span>
        <Button
          style={{ color: highlightColorMapper[value?.color ?? ''] ?? value?.color }}
          icon={
            <Avatar
              style={{ backgroundColor: highlightColorMapper[value?.color ?? ''] ?? value?.color, marginBottom: 2 }}
              size={16}
            />
          }
        >
          {value?.label}

          <DownOutlined
            style={{
              marginLeft: 8,
              color: '#CFCFCF',
            }}
          />
        </Button>
      </Space>
    </Dropdown>
  )
}
